import React, { useEffect, useState } from "react";
import { Checkbox, Flex, Input, Modal, Result, Spin } from "antd";
import { IValidReactivateInvoiceDataResponse } from "../../../interfaces/invoice";
import { InvoiceStatusEnum } from "../../../constants/enum";
import { InvoiceServices } from "../../../services/invoice";
import { useMessageApi } from "../../../context/MessageContext";

interface RejectedModalInvoiceProps {
  isVisible: boolean;
  onClose: (okTriggered?: boolean) => void;
  invoiceDetail: any;
  onGoToEdit: (invoiceDetail: any) => void;
}

const RejectedModalInvoice: React.FC<RejectedModalInvoiceProps> = ({
  isVisible,
  onClose,
  invoiceDetail,
  onGoToEdit,
}) => {
  const [textValue, setTextValue] = useState<string>("");
  const [isReject, setIsReject] = useState(false);
  const [isCanReactivate, setIsCanReactivate] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [aiLimitation, setAiLimitation] = useState(false);
  const messageApi = useMessageApi();

  useEffect(() => {
    const fetchValidReactivate = async () => {
      try {
        setLoading(true);
        const response: IValidReactivateInvoiceDataResponse = await InvoiceServices.validReactivate(invoiceDetail.id);
        const { success, data } = response;
        if (success) {
          setIsCanReactivate(data.canReactivate);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    setIsCanReactivate(true);
    const isCheckReject = invoiceDetail.status === InvoiceStatusEnum.Rejected;
    setIsReject(isCheckReject);
    if (isCheckReject && isVisible) {
      fetchValidReactivate();
    }
    setAiLimitation(invoiceDetail.aiLimitation);
    setTextValue(invoiceDetail.aiLimitationReason ?? "");
  }, [isVisible, invoiceDetail]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTextValue(value);
  };

  const handleOK = async () => {
    if (!isCanReactivate) {
      onGoToEdit(invoiceDetail);
      return;
    }
    try {
      setIsLoadingSubmit(true);
      const response: any = isReject
        ? await InvoiceServices.changeStatusReactivate(invoiceDetail.id)
        : await InvoiceServices.changeStatusReject(invoiceDetail.id, textValue, aiLimitation);
      const { success, message } = response;
      if (success) {
        onClose(true);
        messageApi.success(message || "");
      }
    } catch (error: any) {
      messageApi.error(error?.message);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const renderContent = () => {
    if (isReject) {
      if (loading) {
        return (
          <Flex gap="middle" vertical style={{ padding: "1rem" }}>
            <Spin />
          </Flex>
        );
      }
      return (
        <div style={{ textAlign: "center", fontSize: 20, marginBottom: 24 }}>
          {isCanReactivate
            ? "Do you want to reactivate this invoice ?"
            : "This invoice could not be reactivated due to insufficient information or the cost is being mismatched. Please adjust the invoice to resolve these issues before reactivating"}
        </div>
      );
    } else {
      return (
        <>
          <Result
            status="warning"
            title="By rejecting this Invoice, all of corresponding Invoice Records will also be rejected, do you still want to to proceed ?"
            className="fm-result-modal"
          />
          <Flex vertical gap={10} style={{ marginBottom: 8 }}>
            <Checkbox
              checked={aiLimitation}
              onChange={(e) => {
                setAiLimitation(e.target.checked);
              }}
            >
              AI Limitation
            </Checkbox>
            <span style={{ whiteSpace: "nowrap", marginTop: 10 }}>
              <span className="text-error">*</span> Please enter reason to reject
            </span>
            <Input placeholder="Enter here" value={textValue} onChange={handleInputChange} />
          </Flex>
        </>
      );
    }
  };

  const renderTitle = (
    <>
      {`Confirm ${isReject ? "Reactivate" : "Reject"}`}
      <h4 style={{ color: "var(--text-blue-500)", margin: "0", fontSize: "18px" }}>
        Invoice No. {invoiceDetail.invoiceNumber}
      </h4>
    </>
  );

  return (
    <Modal
      open={isVisible}
      onCancel={() => onClose()}
      onOk={handleOK}
      title={renderTitle}
      okText={isCanReactivate ? "Confirm" : "Go to Edit"}
      okButtonProps={{ disabled: (!isReject && !textValue.trim()) || loading }}
      confirmLoading={isLoadingSubmit}
    >
      {renderContent()}
    </Modal>
  );
};

export default RejectedModalInvoice;
